import { Component, ErrorInfo, ReactNode } from 'react';
import { Flex, Text, ThemeUICSSObject } from 'theme-ui';
import { logError } from 'src/services/datadog';

// The app's only error boundary. Without one, a single render throw unmounts
// the whole tree and leaves a blank page. Placed outside <Suspense> so it
// also catches a React.lazy chunk failing to load (e.g. a stale tab after a
// deploy) — <Suspense> alone doesn't catch that.
//
// Behavior is driven entirely by the `autoReload` prop: off, it shows a
// human a fallback with a manual Reload button, since silently reloading
// could throw away work in progress. On, it restarts itself instead, for
// callers where that risk doesn't apply. This component doesn't know or
// care why a given screen picks one or the other — see the call site for
// that reasoning.

/**
 * Auto-reloads allowed before autoReload gives up and falls back to asking a
 * human. The cap is the point: unbounded, one bad deploy turns every screen
 * running this into a reload loop hammering the backend, which is worse than
 * the blank screen it was meant to fix.
 */
export const MAX_AUTO_RELOADS = 2;

/** Long enough to read "restarting", short enough that nobody gives up. */
export const RELOAD_DELAY_MS = 3000;

/**
 * Used when the crash counter can't be persisted (see recordCrash). The count
 * is what normally stops a loop; without it, a slow loop is the fallback.
 */
export const UNCOUNTED_RELOAD_DELAY_MS = 60000;

/**
 * Crashes further apart than this start a fresh allowance. The cap exists to
 * catch a crash *loop*, not to permanently spend a screen's one recovery on
 * something that happened last week.
 */
export const CRASH_WINDOW_MS = 10 * 60 * 1000;

/**
 * localStorage, not sessionStorage. The counter has to survive the recovery
 * it's counting, and some embedding shells (e.g. a WebView) recreate the page
 * rather than reload it in place — which can drop sessionStorage silently,
 * returning null instead of throwing. The counter would reset to zero on
 * every crash and the cap below would never fire. CRASH_WINDOW_MS keeps a
 * stale count from lingering regardless.
 */
export const CRASH_STORAGE_KEY = 'dmp:crash-reloads';

type CrashRecord = { count: number; at: number };

/**
 * Bumps the persisted crash counter and returns this crash's attempt number, or
 * null if it could not be persisted — some environments disable or throw on
 * storage APIs entirely, and losing the counter must not lose the recovery.
 */
const recordCrash = (): number | null => {
  const now = Date.now();
  let previous = 0;
  try {
    const raw = window.localStorage.getItem(CRASH_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<CrashRecord>;
      if (typeof parsed.at === 'number' && now - parsed.at < CRASH_WINDOW_MS) {
        previous = parsed.count ?? 0;
      }
    }
  } catch {
    return null;
  }
  const attempt = previous + 1;
  try {
    window.localStorage.setItem(
      CRASH_STORAGE_KEY,
      JSON.stringify({ count: attempt, at: now } satisfies CrashRecord),
    );
  } catch {
    return null;
  }
  return attempt;
};

const pageStyle: ThemeUICSSObject = {
  minHeight: '100vh',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '12px',
  padding: '32px',
  textAlign: 'center',
  backgroundColor: '#faf8f5',
};

const titleStyle: ThemeUICSSObject = {
  fontFamily: 'heading',
  fontSize: '28px',
  fontWeight: 800,
  color: '#111111',
};

const bodyStyle: ThemeUICSSObject = {
  fontFamily: 'body',
  fontSize: '17px',
  color: '#8a857c',
  maxWidth: '460px',
};

const buttonStyle: ThemeUICSSObject = {
  marginTop: '18px',
  borderRadius: '16px',
  border: 'none',
  padding: '18px 28px',
  fontSize: '17px',
  fontFamily: 'heading',
  fontWeight: 800,
  color: '#fff',
  backgroundColor: '#111111',
  cursor: 'pointer',
};

export type ErrorBoundaryProps = {
  children: ReactNode;
  /** Prefixes the crash log, e.g. `floor` reports as `floor.crash`. */
  label: string;
  /**
   * Reloads the page itself after a crash, up to MAX_AUTO_RELOADS, then falls
   * back to asking a human. Leave it off for screens where an unprompted
   * reload could throw away work in progress.
   */
  autoReload?: boolean;
  /** Change it to clear the fallback — App passes the current pathname. */
  resetKey?: string;
};

type State = { crashed: boolean; exhausted: boolean };

class ErrorBoundary extends Component<ErrorBoundaryProps, State> {
  public static getDerivedStateFromError(): Partial<State> {
    return { crashed: true };
  }

  public state: State = { crashed: false, exhausted: false };

  private reloadTimer: ReturnType<typeof setTimeout> | null = null;

  public componentDidCatch(error: Error, info: ErrorInfo): void {
    const { label, autoReload } = this.props;
    const attempt = autoReload ? recordCrash() : null;

    // Reported here rather than left to the SDK's console.error capture
    // because only componentDidCatch has the React component stack, which is
    // the one thing that says WHICH component threw.
    logError(`${label}.crash`, error, {
      componentStack: info.componentStack,
      path: window.location.pathname,
      ...(autoReload ? { reloadAttempt: attempt } : {}),
    });

    if (!autoReload) return;
    if (attempt !== null && attempt > MAX_AUTO_RELOADS) {
      this.setState({ exhausted: true });
      return;
    }
    this.reloadTimer = setTimeout(
      () => window.location.reload(),
      attempt === null ? UNCOUNTED_RELOAD_DELAY_MS : RELOAD_DELAY_MS,
    );
  }

  public componentDidUpdate(previous: ErrorBoundaryProps): void {
    if (previous.resetKey !== this.props.resetKey && this.state.crashed) {
      this.setState({ crashed: false, exhausted: false });
    }
  }

  public componentWillUnmount(): void {
    if (this.reloadTimer) clearTimeout(this.reloadTimer);
  }

  public render(): ReactNode {
    const { children, autoReload } = this.props;
    const { crashed, exhausted } = this.state;
    if (!crashed) return children;

    if (autoReload && !exhausted) {
      return (
        <Flex sx={pageStyle} data-test-id="error-boundary">
          <Text sx={titleStyle}>Restarting…</Text>
          <Text sx={bodyStyle}>
            This screen hit a problem and is restarting itself. Nothing you did
            caused it.
          </Text>
        </Flex>
      );
    }

    // Either autoReload is off, or it's on but exhausted its attempts — both
    // need a person, so both get a button.
    return (
      <Flex sx={pageStyle} data-test-id="error-boundary">
        <Text sx={titleStyle}>Something went wrong</Text>
        {exhausted ? (
          <Text sx={bodyStyle} data-test-id="error-boundary-exhausted">
            This screen keeps crashing after restarting. Please let someone
            know, and try again later.
          </Text>
        ) : (
          <Text sx={bodyStyle}>
            This page stopped unexpectedly. Reloading usually fixes it — if it
            keeps happening, let the team know what you were doing.
          </Text>
        )}
        <button
          type="button"
          data-test-id="error-boundary-reload"
          sx={buttonStyle}
          onClick={(): void => window.location.reload()}
        >
          Reload
        </button>
      </Flex>
    );
  }
}

export default ErrorBoundary;
